// src/store/slices/warrantySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import warrantyApi from 'api/warrantyApi';

export const fetchWarranties = createAsyncThunk(
  'warranty/fetchWarranties',
  async ({ page = 1, pageSize = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await warrantyApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load warranties');
    }
  }
);

export const createWarranty = createAsyncThunk(
  'warranty/createWarranty',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await warrantyApi.create(formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to create warranty');
    }
  }
);

export const updateWarranty = createAsyncThunk(
  'warranty/updateWarranty',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await warrantyApi.update(id, formData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Failed to update warranty');
    }
  }
);

export const deleteWarranty = createAsyncThunk(
  'warranty/deleteWarranty',
  async (id, { rejectWithValue }) => {
    try {
      await warrantyApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete warranty');
    }
  }
);

// ────────────────────────────────────────────────
// Additional thunks
// ────────────────────────────────────────────────

export const exportWarranties = createAsyncThunk(
  'warranty/exportWarranties',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await warrantyApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warranties_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

export const importPreview = createAsyncThunk(
  'warranty/importPreview',
  async (file, { rejectWithValue }) => {
    try {
      const data = await warrantyApi.importPreview(file);
      return data; // expected: { preview: [...], errors: [...], total: number, ... }
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Preview failed'
      );
    }
  }
);

export const importConfirm = createAsyncThunk(
  'warranty/importConfirm',
  async (previewData, { rejectWithValue, dispatch }) => {
    try {
      await warrantyApi.importConfirm(previewData);
      dispatch(fetchWarranties({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Import failed'
      );
    }
  }
);

const warrantySlice = createSlice({
  name: 'warranty',
  initialState: {
    warranties: [],
    pagination: { count: 0, page_size: 10 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarranties.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchWarranties.fulfilled, (state, action) => {
        state.isLoading = false;
        state.warranties = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchWarranties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createWarranty.fulfilled, (state, action) => {
        state.warranties.unshift(action.payload);
        state.pagination.count += 1;
      })
      .addCase(updateWarranty.fulfilled, (state, action) => {
        const idx = state.warranties.findIndex(w => w.id === action.payload.id);
        if (idx !== -1) state.warranties[idx] = action.payload;
      })
      .addCase(deleteWarranty.fulfilled, (state, action) => {
        state.warranties = state.warranties.filter(w => w.id !== action.payload);
        state.pagination.count -= 1;
      });
  },
});

export const { clearError } = warrantySlice.actions;
export default warrantySlice.reducer;